import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Observed border-box width of the attached node, in px; `null` until measured.
 *
 * The first measurement happens in the ref callback — the commit phase — so the
 * first paint already knows the width instead of laying out once and correcting
 * itself a frame later. Border box, not content box, so the value does not move
 * when the caller's own padding changes with the layout it is deciding.
 *
 * @returns {[number|null, function]} `[width, ref]`
 */
function useElementWidth() {
  const [width, setWidth] = useState(null)
  const observer = useRef(null)

  const ref = useCallback(node => {
    observer.current?.disconnect()
    observer.current = null
    if (!node) return

    setWidth(node.offsetWidth)

    if (typeof ResizeObserver === 'undefined') return
    observer.current = new ResizeObserver(([entry]) => {
      setWidth(entry.borderBoxSize?.[0]?.inlineSize ?? entry.target.offsetWidth)
    })
    observer.current.observe(node)
  }, [])

  useEffect(() => () => observer.current?.disconnect(), [])

  return [width, ref]
}

export default useElementWidth
